import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  BuildingOfficeIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  CogIcon,
  UsersIcon,
  BuildingOffice2Icon,
  MegaphoneIcon,
  DocumentTextIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
  currentView: 'dashboard' | 'properties' | 'announcements' | 'documents' | 'financial' | 'reports' | 'admin' | 'admin-users' | 'admin-properties' | 'admin-buildings' | 'admin-announcements' | 'admin-documents' | 'admin-financial';
  onViewChange: (view: 'dashboard' | 'properties' | 'announcements' | 'documents' | 'financial' | 'reports' | 'admin' | 'admin-users' | 'admin-properties' | 'admin-buildings' | 'admin-announcements' | 'admin-documents' | 'admin-financial') => void;
}

const Layout: React.FC<LayoutProps> = ({ children, currentView, onViewChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();

  const navigation = [
    { name: 'Dashboard', view: 'dashboard' as const, icon: HomeIcon },
    { name: 'Mis Propiedades', view: 'properties' as const, icon: BuildingOfficeIcon },
    { name: 'Anuncios', view: 'announcements' as const, icon: MegaphoneIcon },
    { name: 'Documentos', view: 'documents' as const, icon: DocumentTextIcon }
  ];

  const adminNavigation = [
    { name: 'Panel Admin', view: 'admin' as const, icon: CogIcon },
    { name: 'Gestionar Usuarios', view: 'admin-users' as const, icon: UsersIcon },
    { name: 'Gestionar Edificios', view: 'admin-buildings' as const, icon: BuildingOfficeIcon },
    { name: 'Gestionar Propiedades', view: 'admin-properties' as const, icon: BuildingOffice2Icon },
    { name: 'Gestionar Anuncios', view: 'admin-announcements' as const, icon: MegaphoneIcon },
    { name: 'Gestionar Documentos', view: 'admin-documents' as const, icon: DocumentTextIcon },
    { name: 'Gestión Financiera', view: 'admin-financial' as const, icon: CurrencyDollarIcon },
  ];

  const isAdmin = user?.tipoSocio === 'administrador';
  const allNavigation = isAdmin ? adminNavigation : navigation;

  const handleLogout = () => {
    logout();
  };

  const handleNavigation = (view: 'dashboard' | 'properties' | 'announcements' | 'documents' | 'financial' | 'reports' | 'admin' | 'admin-users' | 'admin-properties' | 'admin-buildings' | 'admin-announcements' | 'admin-documents' | 'admin-financial') => {
    onViewChange(view);
    setSidebarOpen(false); // Cerrar sidebar en móvil
  };

  // Listen for custom navigation events
  React.useEffect(() => {
    const handleNavigateEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        handleNavigation(customEvent.detail);
      }
    };

    window.addEventListener('navigate', handleNavigateEvent);
    return () => window.removeEventListener('navigate', handleNavigateEvent);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar móvil */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex min-h-16 items-start justify-between px-4 py-2">
            <div className="flex flex-col w-full max-w-[200px]">
              <div className="flex items-center w-full">
                <img 
                  src="/images/logo_esstudia_inversionistas.png" 
                  alt="ESSTUDIA Logo" 
                  className="max-h-14 w-full object-contain"
                />
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex-1 space-y-1 px-2 py-4">
            {allNavigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.view)}
                className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md ${currentView === item.view
                    ? 'bg-hotel-100 text-hotel-700 border-r-2 border-red-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Sidebar desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex min-h-16 items-start px-4 py-2">
            <div className="flex flex-col w-full max-w-[200px]">
              <div className="flex items-center w-full">
                <img 
                  src="/images/logo_esstudia_inversionistas.png" 
                  alt="ESSTUDIA Logo" 
                  className="max-h-14 w-full object-contain"
                />
              </div>
            </div>
          </div>

          <nav className="flex-1 space-y-1 px-2 py-4">
            {allNavigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.view)}
                className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md ${currentView === item.view
                    ? 'bg-hotel-100 text-hotel-700 border-r-2 border-red-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </button>
            ))}
          </nav>

          {/* Perfil del usuario */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-hotel-100 rounded-full flex items-center justify-center">
                <UserIcon className="h-5 w-5 text-hotel-700" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {user?.nombre} {user?.apellido}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.tipoSocio}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-3 w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
            >
              <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="lg:pl-64">
        {/* Header móvil */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-700 hover:text-gray-900"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="flex items-center max-w-[150px]">
                <img 
                  src="/images/logo_esstudia_inversionistas.png" 
                  alt="ESSTUDIA Logo" 
                  className="max-h-10 w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contenido de la página */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
